"use client"

import * as TabsPrimitive from "@radix-ui/react-tabs"
import * as React from "react"
import { cn } from "../utils"

const TabsContext = React.createContext<{ variant?: "line" | "pill" }>({ variant: "line" })

const tabsListBaseClasses = "inline-flex w-fit items-center justify-center"

const tabsListVariants = {
  line: "text-muted-foreground h-11 p-0 relative border-b",
  pill: "bg-muted text-muted-foreground h-9 rounded-lg p-[3px]",
}

const tabsTriggerBaseClasses =
  "relative inline-flex flex-1 items-center justify-center gap-2 text-sm font-medium whitespace-nowrap hover:cursor-pointer transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"

const tabsTriggerVariants = {
  line: [
    "bg-background hover:text-foreground h-full rounded-none border-0 border-b-2 px-4 border-transparent ",
    "data-[state=active]:border-primary dark:data-[state=active]:border-primary data-[state=active]:shadow-none",
  ],
  pill: [
    "h-[calc(100%-1px)] hover:bg-secondary gap-1.5 rounded-md border border-transparent px-2 py-1",
    "text-foreground dark:text-muted-foreground",
    "data-[state=active]:bg-background dark:data-[state=active]:text-foreground data-[state=active]:shadow-sm",
    "dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30",
    "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring focus-visible:ring-[3px] focus-visible:outline-1",
  ],
}

function TabsWrapper({ className, children, ...props }: React.ComponentProps<typeof Tabs>) {
  return (
    <Tabs {...props} className={cn("relative", className)}>
      {children}
    </Tabs>
  )
}

type TabsProps = React.ComponentProps<typeof TabsPrimitive.Root> & {
  variant?: "line" | "pill"
}

function Tabs({ className, variant = "line", ...props }: TabsProps) {
  return (
    <TabsContext.Provider value={{ variant }}>
      <TabsPrimitive.Root
        data-slot="tabs"
        className={cn("flex flex-col gap-4 max-w-full shrink-0", className)}
        {...props}
      />
    </TabsContext.Provider>
  )
}

type TabsListProps = React.ComponentProps<typeof TabsPrimitive.List> & {
  variant?: "line" | "pill"
}

function TabsList({ className, variant, ...props }: TabsListProps) {
  const context = React.useContext(TabsContext)
  const currentVariant = variant ?? context.variant ?? "line"

  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(tabsListBaseClasses, tabsListVariants[currentVariant], className)}
      {...props}
    />
  )
}

type TabsTriggerProps = React.ComponentProps<typeof TabsPrimitive.Trigger> & {
  activeBorderColor?: string
  variant?: "line" | "pill"
}

function TabsTrigger({ className, variant, ...props }: TabsTriggerProps) {
  const context = React.useContext(TabsContext)
  const currentVariant = variant ?? context.variant ?? "line"

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(tabsTriggerBaseClasses, tabsTriggerVariants[currentVariant], className)}
      {...props}
    />
  )
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none min-h-0", className)}
      {...props}
    />
  )
}

export { Tabs, TabsContent, TabsList, TabsPrimitive, TabsTrigger, TabsWrapper }
